import type { DonationType } from "./donationFormStore";

const STORAGE_PREFIX = "donation-form:amount-selection:";
const MAX_AGE_MS = 4 * 60 * 60 * 1000;
const DONATION_TYPES = new Set<`${DonationType}`>([
  "one-off",
  "monthly",
  "recurring",
]);

export type StoredDonationAmountSelection = {
  donationType: `${DonationType}`;
  amount: number;
  hasCustomAmount: boolean;
  selectedAt: number;
};

const storageKey = (formId: number) => `${STORAGE_PREFIX}${formId}`;

export function loadDonationAmountSelection(
  formId: number,
): StoredDonationAmountSelection | null {
  if (typeof window === "undefined") return null;

  try {
    const value = window.localStorage.getItem(storageKey(formId));
    if (!value) return null;

    const parsed = JSON.parse(value) as Partial<StoredDonationAmountSelection>;
    const isDonationType = DONATION_TYPES.has(
      parsed.donationType as DonationType,
    );
    const age =
      typeof parsed.selectedAt === "number"
        ? Date.now() - parsed.selectedAt
        : Number.POSITIVE_INFINITY;
    const isCurrent = age >= 0 && age <= MAX_AGE_MS;

    if (
      !isDonationType ||
      typeof parsed.amount !== "number" ||
      !Number.isFinite(parsed.amount) ||
      parsed.amount <= 0 ||
      typeof parsed.hasCustomAmount !== "boolean" ||
      !isCurrent
    ) {
      window.localStorage.removeItem(storageKey(formId));
      return null;
    }

    return parsed as StoredDonationAmountSelection;
  } catch {
    // Storage can be disabled or contain data written by an older build.
    return null;
  }
}

export function saveDonationAmountSelection(
  formId: number,
  selection: Omit<StoredDonationAmountSelection, "selectedAt">,
): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      storageKey(formId),
      JSON.stringify({ ...selection, selectedAt: Date.now() }),
    );
  } catch {
    // A storage failure must never prevent a donor from continuing.
  }
}

export function clearDonationAmountSelection(formId: number): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(storageKey(formId));
  } catch {
    // A storage failure must never affect the payment result.
  }
}
